import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import DestinoMockup from "@/components/DestinoMockups";
import { getProject, getNeighbours } from "@/lib/projects";

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const p = getProject(slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (!p) {
    return (
      <div className="min-h-screen flex flex-col">
        <Nav />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-lg">
            <div className="font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)] mb-6">Error 404</div>
            <h1 className="font-display text-6xl lg:text-8xl leading-[0.95] tightest pb-2">
              Este destino <span className="font-italic-display">no existe.</span>
            </h1>
            <button onClick={() => navigate("/")} className="btn-primary mt-10">Volver al inicio</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { next } = getNeighbours(slug);

  return (
    <div className="relative">
      <Nav />

      {/* Meta + titular */}
      <section className="pt-32 lg:pt-44 pb-10 lg:pb-16">
        <div className="max-w-[95%] mx-auto">
          <div className="flex items-center justify-between font-sans text-[11px] tracking-[0.22em] uppercase text-[color:var(--ink-60)] mb-14">
            <Link to="/#work" className="link-underline">← Destinos</Link>
            <span>{p.n} · Concepto</span>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:gap-16 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)] mb-6">
                {p.sector}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[5.2vw] leading-[1.02] tightest pb-2">
                {p.titular}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Animated mockup hero */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="max-w-[95%] mx-auto">
          <motion.div style={{ scale }} className="relative aspect-[16/10] lg:aspect-[21/10] border border-[color:var(--ink-10)] overflow-hidden" data-cursor="hover">
            <div className="absolute inset-0 max-w-[520px] mx-auto">
              <DestinoMockup type={p.mockup} accent={p.accent} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filosofía */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[95%] mx-auto grid grid-cols-12 gap-6 lg:gap-16">
          <div className="col-span-12 lg:col-span-3 font-sans text-[11px] tracking-[0.22em] uppercase text-[color:var(--ink-60)]">
            Filosofía del proyecto
          </div>
          <div className="col-span-12 lg:col-span-8">
            {p.descripcion.map((par, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: i * 0.1 }}
                className={`font-display leading-[1.25] tighter pb-1 ${i === 0 ? "text-2xl lg:text-4xl" : "text-xl lg:text-2xl text-[color:var(--ink)]/75 mt-8"}`}
              >
                {par}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {p.gallery && (
        <section className="pb-20 lg:pb-32">
          <div className="max-w-[95%] mx-auto grid grid-cols-12 gap-4 lg:gap-8">
            {p.gallery.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: i * 0.06 }}
                className={`overflow-hidden ${i === 0 ? "col-span-12 lg:col-span-8" : i === 1 ? "col-span-12 lg:col-span-4" : "col-span-12"}`}
                data-cursor="image"
              >
                <img src={src} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.12)" }} loading="lazy" />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Incluye */}
      <section className="py-20 lg:py-28" style={{ background: "#F2EFEB" }}>
        <div className="max-w-[95%] mx-auto grid grid-cols-12 gap-6 lg:gap-16">
          <div className="col-span-12 lg:col-span-3 font-sans text-[11px] tracking-[0.22em] uppercase text-[color:var(--ink-60)]">
            Incluye
          </div>
          <div className="col-span-12 lg:col-span-9">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
              {p.incluye.map((it) => (
                <li key={it} className="flex items-center gap-4 border-b border-[color:var(--ink-10)] pb-4 font-display text-xl lg:text-2xl tighter">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#FF6B2C" }} />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Next destino */}
      <section className="section-dark py-20">
        <div className="max-w-[95%] mx-auto">
          {next && (
            <Link
              to={`/proyectos/${next.slug}`}
              data-testid="case-nav-next"
              className="group block border border-white/10 p-8 lg:p-14 hover:bg-white/[0.04] transition-colors duration-500"
            >
              <div className="font-sans text-[11px] tracking-[0.22em] uppercase opacity-60">Descubrir proyecto →</div>
              <div className="mt-6 font-display text-4xl lg:text-6xl leading-[1.02] tightest pb-1">{next.card}</div>
              <div className="mt-3 font-sans text-sm opacity-70">{next.sector}</div>
            </Link>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
